import { heicToJpeg } from "heic-converter";
import { uploadAndSendFile } from "./media";

/**
 * Shows a fullscreen attachment preview overlay before sending.
 * HEIC images are converted to JPEG at preview time so the user sees the
 * converted result and the already-converted file is sent without re-conversion.
 *
 * @param {File} file - The file to preview
 * @param {object} client - Matrix client instance
 * @param {string|Function} roomId - Target room ID, or a resolver for it (a draft has no room until this send)
 * @param {HTMLElement} composerEl - The composer element (for uploadAndSendFile)
 * @param {Function} t - Translation function (key, fallback) => string
 * @returns {{ close: Function }} handle to programmatically close the preview
 */
export async function showAttachmentPreview(file, client, roomId, composerEl, t) {
  // Remove any existing preview
  const existing = document.querySelector(".bryght-attachment-preview");
  if (existing) existing.remove();

  const chatContainer = document.getElementById("chat-container");
  if (!chatContainer) return { close: () => {} };

  // ── HEIC conversion (before preview) ────────────────────
  let convertedFile = file;
  const isHeic = file.type === "image/heic" || file.name.toLowerCase().endsWith(".heic");

  if (isHeic) {
    try {
      const finalBlob = await heicToJpeg(file, { quality: 0.8 });
      const newFilename = file.name.replace(/\.heic$/i, ".jpeg");
      convertedFile = new File([finalBlob], newFilename, { type: "image/jpeg" });
    } catch (err) {
      console.warn("[BryghtChat] HEIC conversion failed in preview, using original:", err);
    }
  }

  const overlay = document.createElement("div");
  overlay.className = "bryght-attachment-preview";

  // ── Header ──────────────────────────────────────────────
  const header = document.createElement("div");
  header.className = "bryght-attachment-preview__header";

  const closeBtn = document.createElement("button");
  closeBtn.className = "bryght-attachment-preview__close";
  closeBtn.setAttribute("aria-label", t("close", "Schließen"));
  closeBtn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>`;
  closeBtn.addEventListener("click", close);

  const titleEl = document.createElement("span");
  titleEl.className = "bryght-attachment-preview__title";
  titleEl.textContent = convertedFile.name;

  header.appendChild(closeBtn);
  header.appendChild(titleEl);

  // ── Body (preview area) ─────────────────────────────────
  const body = document.createElement("div");
  body.className = "bryght-attachment-preview__body";

  const isImage = convertedFile.type.startsWith("image/");
  const isVideo = convertedFile.type.startsWith("video/");
  const blobUrl = URL.createObjectURL(convertedFile);

  if (isImage) {
    const img = document.createElement("img");
    img.className = "bryght-attachment-preview__media";
    img.src = blobUrl;
    img.alt = convertedFile.name;
    body.appendChild(img);
  } else if (isVideo) {
    const video = document.createElement("video");
    video.className = "bryght-attachment-preview__media";
    video.src = blobUrl;
    video.controls = true;
    video.playsInline = true;
    video.preload = "metadata";
    body.appendChild(video);
  } else {
    // Document placeholder
    const docContainer = document.createElement("div");
    docContainer.className = "bryght-attachment-preview__doc";

    const iconEl = document.createElement("div");
    iconEl.className = "bryght-attachment-preview__doc-icon";
    const ext = convertedFile.name.split(".").pop()?.toUpperCase() || "FILE";
    iconEl.innerHTML = `
      <svg width="64" height="80" viewBox="0 0 64 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 0C1.79 0 0 1.79 0 4V76C0 78.21 1.79 80 4 80H60C62.21 80 64 78.21 64 76V20L44 0H4Z" fill="#e0e0e0"/>
        <path d="M44 0V16C44 18.21 45.79 20 48 20H64L44 0Z" fill="#bdbdbd"/>
        <text x="32" y="52" text-anchor="middle" font-size="14" font-weight="600" fill="#757575">${ext}</text>
      </svg>`;

    const nameEl = document.createElement("div");
    nameEl.className = "bryght-attachment-preview__doc-name";
    nameEl.textContent = convertedFile.name;

    const sizeEl = document.createElement("div");
    sizeEl.className = "bryght-attachment-preview__doc-size";
    sizeEl.textContent = formatSize(convertedFile.size);

    docContainer.append(iconEl, nameEl, sizeEl);
    body.appendChild(docContainer);
  }

  // ── Footer ──────────────────────────────────────────────
  const footer = document.createElement("div");
  footer.className = "bryght-attachment-preview__footer";

  const captionInput = document.createElement("input");
  captionInput.type = "text";
  captionInput.className = "bryght-attachment-preview__caption";
  captionInput.placeholder = t("add_caption_placeholder", "Nachricht hinzufügen…");
  captionInput.autocomplete = "off";

  const sendBtn = document.createElement("button");
  sendBtn.className = "bryght-attachment-preview__send";
  sendBtn.setAttribute("aria-label", t("send", "Senden"));
  sendBtn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></svg>`;

  const doSend = () => {
    const caption = captionInput.value.trim();
    close();
    // Send the already-converted file (no re-conversion needed in uploadAndSendFile)
    uploadAndSendFile(client, roomId, convertedFile, composerEl, caption, t);
  };

  sendBtn.addEventListener("click", doSend);
  captionInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") { e.preventDefault(); doSend(); }
  });

  footer.append(captionInput, sendBtn);

  // ── Assemble ────────────────────────────────────────────
  overlay.append(header, body, footer);
  chatContainer.appendChild(overlay);

  // Animate in and focus caption input
  requestAnimationFrame(() => {
    overlay.classList.add("bryght-attachment-preview--visible");
    captionInput.focus();
  });

  // Close on Escape
  const onKeydown = (e) => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", onKeydown);

  let isClosed = false;
  function close() {
    if (isClosed) return;
    isClosed = true;
    document.removeEventListener("keydown", onKeydown);
    overlay.classList.remove("bryght-attachment-preview--visible");
    // Wait for fade-out animation
    setTimeout(() => {
      overlay.remove();
      URL.revokeObjectURL(blobUrl);
    }, 200);
  }

  return { close };
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
